import types from '../../mutation-types';
import getters, { getSelectedChatConversation } from './getters';
import actions from './actions';
import { findPendingMessageIndex } from './helpers';
import { MESSAGE_STATUS } from 'shared/constants/messages';
import wootConstants from 'dashboard/constants/globals';
import { BUS_EVENTS } from '../../../../shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import { CONTENT_TYPES } from 'dashboard/components-next/message/constants.js';

const MAX_CACHED_CONVERSATIONS = 10;
const MAX_MESSAGES_PER_CONVERSATION = 200;
const MAX_CONVERSATIONS_IN_STORE = 150;

const state = {
  allConversations: [],
  recentlyViewedIds: [],
  attachments: {},
  listLoadingStatus: true,
  chatStatusFilter: wootConstants.STATUS_TYPE.OPEN,
  chatSortFilter: wootConstants.SORT_BY_TYPE.LATEST,
  currentInbox: null,
  selectedChatId: null,
  appliedFilters: [],
  contextMenuChatId: null,
  conversationParticipants: [],
  conversationLastSeen: null,
  syncConversationsMessages: {},
  conversationFilters: {},
  copilotAssistant: {},
  // Ids que a lista atual esta EXIBINDO (vem dos contextos do
  // conversationResults): a poda LRU nunca pode descartar esses — foi o
  // assassino da paginacao ("trava nos 150", qualquer conta/aparelho).
  protectedConversationIds: [],
};

const getConversationById = _state => conversationId => {
  return _state.allConversations.find(c => c.id === conversationId);
};

// mutations
export const mutations = {
  [types.SET_ALL_CONVERSATION](_state, conversationList) {
    const newAllConversations = [..._state.allConversations];
    conversationList.forEach(conversation => {
      const indexInCurrentList = newAllConversations.findIndex(
        c => c.id === conversation.id
      );
      if (indexInCurrentList < 0) {
        newAllConversations.push(conversation);
      } else {
        const existingConversation = newAllConversations[indexInCurrentList];
        const isInCache =
          conversation.id === _state.selectedChatId ||
          _state.recentlyViewedIds.includes(conversation.id);

        if (isInCache && existingConversation.messages?.length) {
          // Preserve messages, dataFetched, allMessagesLoaded for cached conversations
          newAllConversations[indexInCurrentList] = {
            ...conversation,
            allMessagesLoaded: existingConversation.allMessagesLoaded,
            messages: existingConversation.messages,
            dataFetched: existingConversation.dataFetched,
          };
        } else {
          // Non-cached conversations: replace entirely (API sends last message)
          newAllConversations[indexInCurrentList] = conversation;
        }
      }
    });
    // Cap total conversations to prevent unbounded memory growth
    if (newAllConversations.length > MAX_CONVERSATIONS_IN_STORE) {
      // Sort by last_activity_at desc, keep most recent ones
      newAllConversations.sort(
        (a, b) =>
          new Date(b.last_activity_at || 0) - new Date(a.last_activity_at || 0)
      );
      // Poda os mais antigos — mas NUNCA a selecionada, nem o que a lista
      // esta exibindo, NEM a pagina que ACABOU de chegar (a protecao dos
      // contextos so e gravada depois deste commit — sem esta linha a
      // pagina nova, sendo a mais antiga do sort, era descartada NO ATO
      // da chegada e a protecao corria sempre uma pagina atrasada:
      // protegidos crescia guardando fantasmas e a tela nao passava de 150)
      const protegidos = new Set(_state.protectedConversationIds);
      conversationList.forEach(c => protegidos.add(c.id));
      const kept = newAllConversations.filter(
        (c, i) =>
          i < MAX_CONVERSATIONS_IN_STORE ||
          c.id === _state.selectedChatId ||
          protegidos.has(c.id)
      );
      _state.allConversations = kept;
    } else {
      _state.allConversations = newAllConversations;
    }
  },
  [types.SET_PROTECTED_CONVERSATION_IDS](_state, ids) {
    _state.protectedConversationIds = ids || [];
  },
  [types.EMPTY_ALL_CONVERSATION](_state) {
    _state.allConversations = [];
    _state.recentlyViewedIds = [];
    _state.selectedChatId = null;
  },
  [types.SET_ALL_MESSAGES_LOADED](_state, conversationId) {
    const chat = getConversationById(_state)(conversationId);
    if (chat) {
      chat.allMessagesLoaded = true;
    }
  },

  [types.CLEAR_ALL_MESSAGES_LOADED](_state, conversationId) {
    const chat = getConversationById(_state)(conversationId);
    if (chat) {
      chat.allMessagesLoaded = false;
    }
  },
  [types.CLEAR_CURRENT_CHAT_WINDOW](_state) {
    _state.selectedChatId = null;
  },

  [types.SET_PREVIOUS_CONVERSATIONS](_state, { id, data }) {
    if (data.length) {
      const [chat] = _state.allConversations.filter(c => c.id === id);
      // Deduplicate: only add messages not already present (by ID)
      const existingIds = new Set(chat.messages.map(m => m.id));
      const newMessages = data.filter(m => !existingIds.has(m.id));
      if (newMessages.length) {
        chat.messages.unshift(...newMessages);
      }
      // Limit messages per conversation to prevent unbounded growth
      if (chat.messages.length > MAX_MESSAGES_PER_CONVERSATION) {
        chat.messages = chat.messages.slice(-MAX_MESSAGES_PER_CONVERSATION);
      }
    }
  },

  [types.TRACK_CONVERSATION_VIEW](_state, conversationId) {
    // Update LRU list — move this conversation to the front
    const idx = _state.recentlyViewedIds.indexOf(conversationId);
    if (idx > -1) {
      _state.recentlyViewedIds.splice(idx, 1);
    }
    _state.recentlyViewedIds.unshift(conversationId);

    // Evict messages from conversations beyond the cache limit
    if (_state.recentlyViewedIds.length > MAX_CACHED_CONVERSATIONS) {
      const evictIds = _state.recentlyViewedIds.splice(
        MAX_CACHED_CONVERSATIONS
      );
      evictIds.forEach(evictId => {
        // Don't evict the active conversation
        if (evictId === _state.selectedChatId) return;
        const chat = _state.allConversations.find(c => c.id === evictId);
        if (!chat) return;
        // Clear messages and reset fetch flags so they reload when revisited
        chat.messages = [];
        chat.dataFetched = undefined;
        chat.allMessagesLoaded = false;
      });
    }
  },
  [types.SET_ALL_ATTACHMENTS](_state, { id, data }) {
    _state.attachments[id] = [...data];
  },
  [types.SET_MISSING_MESSAGES](_state, { id, data }) {
    const [chat] = _state.allConversations.filter(c => c.id === id);
    if (!chat) return;
    chat.messages = data;
  },

  [types.SET_CHAT_DATA_FETCHED](_state, conversationId) {
    const chat = getConversationById(_state)(conversationId);
    if (chat) {
      chat.dataFetched = true;
    }
  },

  [types.SET_CURRENT_CHAT_WINDOW](_state, activeChat) {
    if (activeChat) {
      _state.selectedChatId = activeChat.id;
    }
  },

  [types.ASSIGN_AGENT](_state, { conversationId, assignee }) {
    const chat = getConversationById(_state)(conversationId);
    if (chat) {
      chat.meta.assignee = assignee;
    }
  },

  [types.ASSIGN_TEAM](_state, { team, conversationId }) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    chat.meta.team = team;
  },

  [types.UPDATE_CONVERSATION_LAST_ACTIVITY](
    _state,
    { lastActivityAt, conversationId }
  ) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    if (chat) {
      chat.last_activity_at = lastActivityAt;
    }
  },
  [types.ASSIGN_PRIORITY](_state, { priority, conversationId }) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    chat.priority = priority;
  },

  [types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES](
    _state,
    { conversationId, customAttributes }
  ) {
    const conversation = _state.allConversations.find(
      c => c.id === conversationId
    );
    if (conversation) {
      conversation.custom_attributes = {
        ...conversation.custom_attributes,
        ...customAttributes,
      };
    }
  },

  [types.CHANGE_CONVERSATION_STATUS](
    _state,
    { conversationId, status, snoozedUntil }
  ) {
    const conversation =
      getters.getConversationById(_state)(conversationId) || {};
    conversation.snoozed_until = snoozedUntil;
    conversation.status = status;
  },

  [types.MUTE_CONVERSATION](_state) {
    const [chat] = getSelectedChatConversation(_state);
    chat.muted = true;
  },

  [types.UNMUTE_CONVERSATION](_state) {
    const [chat] = getSelectedChatConversation(_state);
    chat.muted = false;
  },

  [types.ADD_CONVERSATION_ATTACHMENTS](_state, message) {
    // early return if the message has not been sent, or has no attachments
    if (
      message.status !== MESSAGE_STATUS.SENT ||
      !message.attachments?.length
    ) {
      return;
    }

    const id = message.conversation_id;
    const existingAttachments = _state.attachments[id] || [];

    const attachmentsToAdd = message.attachments.filter(attachment => {
      // if the attachment is not already in the store, add it
      // this is to prevent duplicates
      return !existingAttachments.some(
        existingAttachment => existingAttachment.id === attachment.id
      );
    });

    // replace the attachments in the store
    _state.attachments[id] = [...existingAttachments, ...attachmentsToAdd];
  },

  [types.DELETE_CONVERSATION_ATTACHMENTS](_state, message) {
    if (message.status !== MESSAGE_STATUS.SENT) return;

    const { conversation_id: id } = message;
    const existingAttachments = _state.attachments[id] || [];
    if (!existingAttachments.length) return;

    _state.attachments[id] = existingAttachments.filter(attachment => {
      return attachment.message_id !== message.id;
    });
  },

  [types.ADD_MESSAGE](
    { allConversations, selectedChatId, recentlyViewedIds },
    message
  ) {
    const { conversation_id: conversationId } = message;
    const [chat] = getSelectedChatConversation({
      allConversations,
      selectedChatId: conversationId,
    });
    if (!chat) return;

    const pendingMessageIndex = findPendingMessageIndex(chat, message);
    if (pendingMessageIndex !== -1) {
      chat.messages[pendingMessageIndex] = message;
    } else {
      const isInCache =
        conversationId === selectedChatId ||
        recentlyViewedIds.includes(conversationId);

      // Atualizacao (status/lida) de uma mensagem ANTIGA que nao esta na
      // lista carregada: entra na posicao certa, nunca no fim — senao a
      // conversa mostra uma mensagem das 15h embaixo das 17h. Fora do cache
      // (so a previa da lista) uma antiga nem entra.
      const last = chat.messages[chat.messages.length - 1];
      const isOlder = last && message.id && message.id < last.id;
      if (isOlder) {
        if (!isInCache) return;
        const at = chat.messages.findIndex(m => m.id > message.id);
        chat.messages.splice(at === -1 ? chat.messages.length : at, 0, message);
        return;
      }

      chat.messages.push(message);
      if (isInCache) {
        // Cached conversations: keep up to MAX_MESSAGES_PER_CONVERSATION
        if (chat.messages.length > MAX_MESSAGES_PER_CONVERSATION) {
          chat.messages = chat.messages.slice(-MAX_MESSAGES_PER_CONVERSATION);
        }
      } else if (chat.messages.length > 1) {
        // Non-cached conversations: only keep the last message (sidebar preview)
        chat.messages = chat.messages.slice(-1);
      }
      chat.timestamp = message.created_at;
      const { conversation: { unread_count: unreadCount = 0 } = {} } = message;
      chat.unread_count = unreadCount;
      if (selectedChatId === conversationId) {
        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
      }
    }
  },

  [types.ADD_CONVERSATION](_state, conversation) {
    _state.allConversations.push(conversation);
  },

  [types.DELETE_CONVERSATION](_state, conversationId) {
    _state.allConversations = _state.allConversations.filter(
      c => c.id !== conversationId
    );
    delete _state.attachments[conversationId];
    delete _state.syncConversationsMessages[conversationId];
  },

  [types.UPDATE_CONVERSATION](_state, conversation) {
    const { allConversations } = _state;
    const index = allConversations.findIndex(c => c.id === conversation.id);

    if (index > -1) {
      const selectedConversation = allConversations[index];

      // ignore out of order events
      if (conversation.updated_at < selectedConversation.updated_at) {
        return;
      }

      const { messages, ...updates } = conversation;
      allConversations[index] = {
        ...selectedConversation,
        ...updates,
        messages: selectedConversation.messages || messages || [],
      };
      if (_state.selectedChatId === conversation.id) {
        emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
      }
    } else {
      _state.allConversations.push(conversation);
    }
  },

  [types.SET_LIST_LOADING_STATUS](_state) {
    _state.listLoadingStatus = true;
  },

  [types.CLEAR_LIST_LOADING_STATUS](_state) {
    _state.listLoadingStatus = false;
  },

  [types.UPDATE_MESSAGE_UNREAD_COUNT](
    _state,
    { id, lastSeen, unreadCount = 0 }
  ) {
    const [chat] = _state.allConversations.filter(c => c.id === id);
    if (chat) {
      chat.agent_last_seen_at = lastSeen;
      chat.unread_count = unreadCount;
    }
  },
  [types.CHANGE_CHAT_STATUS_FILTER](_state, data) {
    _state.chatStatusFilter = data;
  },

  [types.CHANGE_CHAT_SORT_FILTER](_state, data) {
    _state.chatSortFilter = data;
  },

  // Update assignee on action cable message
  [types.UPDATE_ASSIGNEE](_state, payload) {
    const chat = getConversationById(_state)(payload.id);
    if (chat) {
      chat.meta.assignee = payload.assignee;
    }
  },

  [types.UPDATE_CONVERSATION_CONTACT](_state, { conversationId, ...payload }) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    if (chat) {
      chat.meta.sender = payload;
    }
  },

  [types.UPDATE_CONVERSATION_CALL_STATUS](
    _state,
    { conversationId, callStatus }
  ) {
    const chat = getConversationById(_state)(conversationId);
    if (!chat) return;

    chat.additional_attributes = {
      ...chat.additional_attributes,
      call_status: callStatus,
    };
  },

  [types.UPDATE_MESSAGE_CALL_STATUS](_state, { conversationId, callStatus }) {
    const chat = getConversationById(_state)(conversationId);
    if (!chat) return;

    const lastCall = (chat.messages || []).findLast(
      m => m.content_type === CONTENT_TYPES.VOICE_CALL
    );

    if (!lastCall) return;

    lastCall.content_attributes ??= {};
    lastCall.content_attributes.data = {
      ...lastCall.content_attributes.data,
      status: callStatus,
    };
  },

  [types.SET_ACTIVE_INBOX](_state, inboxId) {
    _state.currentInbox = inboxId ? parseInt(inboxId, 10) : null;
  },

  [types.SET_CONVERSATION_CAN_REPLY](_state, { conversationId, canReply }) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    if (chat) {
      chat.can_reply = canReply;
    }
  },

  // Atualiza a expiração da janela de mensagens em tempo real (ex.: quando
  // chega mensagem do cliente, a janela de 24h reabre). A janela só estende
  // pra frente — nunca encurta.
  [types.SET_CONVERSATION_MESSAGING_WINDOW](
    _state,
    { conversationId, expiresAt }
  ) {
    const [chat] = _state.allConversations.filter(c => c.id === conversationId);
    if (
      chat &&
      (!chat.messaging_window_expires_at ||
        expiresAt > chat.messaging_window_expires_at)
    ) {
      chat.messaging_window_expires_at = expiresAt;
    }
  },

  [types.CLEAR_CONTACT_CONVERSATIONS](_state, contactId) {
    const chats = _state.allConversations.filter(
      c => c.meta.sender.id !== contactId
    );
    _state.allConversations = chats;
  },

  [types.SET_CONVERSATION_FILTERS](_state, data) {
    _state.appliedFilters = data;
  },

  [types.CLEAR_CONVERSATION_FILTERS](_state) {
    _state.appliedFilters = [];
  },

  [types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION](
    _state,
    { conversationId, messageId }
  ) {
    _state.syncConversationsMessages[conversationId] = messageId;
  },

  [types.SET_CONTEXT_MENU_CHAT_ID](_state, chatId) {
    _state.contextMenuChatId = chatId;
  },

  [types.SET_CHAT_LIST_FILTERS](_state, data) {
    _state.conversationFilters = data;
  },
  [types.UPDATE_CHAT_LIST_FILTERS](_state, data) {
    _state.conversationFilters = { ..._state.conversationFilters, ...data };
  },
  [types.SET_INBOX_CAPTAIN_ASSISTANT](_state, data) {
    _state.copilotAssistant = data.assistant;
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
